import { compose } from 'redux';
import type { ConfigProps, FormStateMap } from 'redux-form';
import { change as changeFormValue, formValueSelector, reduxForm } from 'redux-form';
import type { MapDispatchToProps, MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import type { ComponentType } from 'react';
import type React from 'react';
import type { Cadillac, TripCharacteristics } from '@eti/schema-types';
import { fillPaymentForm } from '../../../common/debug/utils';
import { PAYMENT } from '../../../constants/formNamesConstants';
import PaymentPage from '../entry-page/components/PaymentPage';
import { scrollToAndFocusOnFirstError } from '../../../utils/scrollToElement';
import { reduxFields, TAX_ID } from '../constants/formFields';
import { withValidationProvider } from '../higher-order-components/WithValidationProvider';
import {
  getCardSchemaId,
  getCardTypeId,
  getCommercialCardTypeId,
} from '../payment-methods/card/utils/formSelectors';
import {
  getSelectedPaymentMethodId,
  getSelectedPaymentMethod,
} from '../entry-page/utils/paymentMethod';
import { paymentMethodCodes, paymentMethodTypes } from '../constants/paymentMethods';
import type {
  TransformedPaymentMethod,
  BoundsType,
  CabinBaggageTripSummary,
  CartType,
  CheckedBaggageSummary,
  FareRule,
  IncludedCabinBaggage,
  IncludedExtraProducts,
  PersonalItemTripSummary,
  RefetchPaymentQuery,
  TransformedPaymentMethodPrice,
  Travelers,
  TripChanges,
  TripProviderSystems,
} from '../models';
import type { ValidationTexts } from '../hooks/useValidationTexts';

import type { Bundles } from '../../../common/data-layer/ecommerce/models';
import type { AddressForm, SelectedMethodForm } from '../utils/initialValues';
import type {
  TransformedPaymentLinkProduct,
  TransformedProduct,
} from '../entry-page/utils/ancillaryProducts';
import type { TravelerPrice } from '../../traveler-details/models';

const {
  PRICE_CHANGE_DISPLAYED,
  SELECTED_PAYMENT_METHOD,
  SELECTED_PAYMENT_METHOD_SUB_SECTION,
  METHOD_SUBSECTION_CODE,
} = reduxFields;

// TODO: These three interfaces in most probably the props definition for PaymentPage.tsx - They should be moved there in CFCE-1182
interface BaseProps {
  headerAndInfoHeight: number;
  setIsMenuVisible: (pathname: { type: string }) => void;
  setHasPendingPayment: React.Dispatch<React.SetStateAction<boolean>>;
  validationTexts: ValidationTexts;

  brand: string;
  cadillac?: Cadillac | null;
  chargeCurrencyPriceFormat: CartType['chargeCurrencyPriceFormat'];
  initialValues: AddressForm & NonNullable<SelectedMethodForm>;
  isOtherChargeCurrency?: boolean | null;
  isPanAndDoBDisabled?: boolean | null;
  market: string;
  paymentMethodPrices: TransformedPaymentMethodPrice[];
  paymentMethods: TransformedPaymentMethod[];
  refetchPaymentQuery: RefetchPaymentQuery;
  transactionId?: string | null;
}

interface PaymentLinkProps {
  isPaymentLinkCart: true;
  isSnowplowTrackingEnabled?: false;
  paymentLinkCartId: string | null | undefined;
  paymentLinkCartPrice: number | undefined;
  products?: TransformedPaymentLinkProduct[];
}

interface PaymentProps {
  bounds?: BoundsType;
  bundles: Bundles;
  cabinBaggageTripSummary: CabinBaggageTripSummary;
  cartId?: string | null;
  checkedBaggageSummary: CheckedBaggageSummary;
  customerContactInfo: CartType['customerContactInfo'];
  customerEmail?: string | null;
  customerPhone?: string | null;
  discountAmount?: number;
  fareRules: FareRule;
  hasPotentialForAdditionalTax?: boolean;
  includedCabinBaggage?: IncludedCabinBaggage;
  includedExtraProducts?: IncludedExtraProducts;
  isMultiStop: boolean;
  isPaymentLinkCart?: false;
  isSnowplowTrackingEnabled: boolean;
  onDeclineTripChange: () => void;
  personalItemTripSummary: PersonalItemTripSummary;
  preloadReceiptPage: (inView: boolean) => Promise<void>;
  products?: TransformedProduct[];
  selectionKey?: string;
  sendTrackEvent: (params: { category: string; action: string; label: string }) => void;
  serviceFee?: CartType['serviceFee'];
  states?: string[];
  subtotalPrice?: number;
  travelerPrices?: TravelerPrice[];
  travelers?: Travelers;
  tripChanges: TripChanges;
  tripCharacteristics?: TripCharacteristics[];
  tripProviderSystems?: TripProviderSystems;
}

export type PaymentPageContainerProps = BaseProps & (PaymentLinkProps | PaymentProps);

interface State {
  form: FormStateMap;
}

const onSubmitFail: ConfigProps<State, PaymentPageContainerProps>['onSubmitFail'] = (
  _errors,
  _dispatch,
  _submitError,
  props,
) => {
  const isPaymentPage = window.location.pathname.includes('/payment');

  if (props.isSnowplowTrackingEnabled && isPaymentPage) {
    props.sendTrackEvent({
      category: 'Payment',
      action: 'Error',
      label: 'Validation Error',
    });
  }
  scrollToAndFocusOnFirstError();
};

const connectFormToState = reduxForm({
  form: PAYMENT,
  touchOnChange: true,
  touchOnBlur: true,
  onSubmitFail,
});

type ConnectedProps = {
  selectedCommercialCardId: string | null;
  selectedMainPaymentMethod: { code: string; type: string };
  selectedPaymentMethodCode: string;
  selectedPaymentMethodId: string;
  selectedPaymentMethodType: string;
  selectedSubsectionCode: string;
};

const mapStateToProps: MapStateToProps<ConnectedProps, PaymentPageContainerProps, State> = (
  state,
  { paymentMethods },
) => {
  const selectedCardId = getCardTypeId(PAYMENT)(state) || null;
  const selectedCommercialCardId = getCommercialCardTypeId(PAYMENT)(state) || null;
  const selectedMainPaymentMethod = formValueSelector(PAYMENT)(state, SELECTED_PAYMENT_METHOD);
  const selectedPaymentMethodCode = selectedMainPaymentMethod?.code;
  const selectedPaymentMethodType = selectedMainPaymentMethod?.type;
  const selectedSubsectionCode =
    selectedMainPaymentMethod?.[SELECTED_PAYMENT_METHOD_SUB_SECTION]?.[METHOD_SUBSECTION_CODE];

  const selectedCardSchemaId =
    selectedPaymentMethodType === paymentMethodTypes.CARD &&
    selectedPaymentMethodCode === paymentMethodCodes.CARD
      ? getCardSchemaId(PAYMENT)(state)
      : undefined;

  const selectedPaymentMethod = getSelectedPaymentMethod(
    selectedPaymentMethodCode,
    selectedPaymentMethodType,
    undefined,
    selectedCommercialCardId,
    paymentMethods,
    selectedSubsectionCode,
  );

  const selectedPaymentMethodId = getSelectedPaymentMethodId(
    selectedPaymentMethodType,
    selectedCardSchemaId ? Number(selectedCardSchemaId) : selectedCardId,
    selectedMainPaymentMethod?.id ?? selectedPaymentMethod?.id,
    paymentMethods,
  );

  return {
    selectedCommercialCardId,
    selectedMainPaymentMethod,
    selectedPaymentMethodCode,
    selectedPaymentMethodId,
    selectedPaymentMethodType,
    selectedSubsectionCode,
  };
};

type ConnectedDispatchProps = {
  populateTaxId: (taxId: string) => void;
  fillForm: (change: any) => void;
  populatePriceChangeDisplayed: (price: number) => void;
};

const mapDispatchToProps: MapDispatchToProps<ConnectedDispatchProps, PaymentPageContainerProps> = (
  dispatch,
) => ({
  populateTaxId: (taxId) => dispatch(changeFormValue(PAYMENT, TAX_ID, taxId)),
  fillForm: (change) => {
    fillPaymentForm(dispatch, change);
  },
  populatePriceChangeDisplayed: (price) => {
    dispatch(changeFormValue(PAYMENT, PRICE_CHANGE_DISPLAYED, price));
  },
});

export default compose<ComponentType<PaymentPageContainerProps>>(
  withValidationProvider,
  connect(mapStateToProps, mapDispatchToProps),
  connectFormToState,
)(PaymentPage);
